/**
 * PATH: packages/ui/src/components/settings/email-accounts-tab/SharedAccessEditor.tsx
 * PURPOSE: Owner-only editor for who a SHARED mailbox is shared with.
 * WHY: Operator ask (2026-06-17) — manage the access list (add/remove org
 *      members) from the Settings -> Integrations mailbox card.
 *
 * FLOW:
 *   ┌──────────────────┐     ┌───────────────────┐     ┌──────────────────┐
 *   │ access_grants +  │  →  │ SharedAccessEditor │  →  │ onAddGrant /     │
 *   │ org members      │     │                    │     │ onRemoveGrant    │
 *   └──────────────────┘     └───────────────────┘     └──────────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - ../../../lib/utils: cn
 *   - ./email-accounts-tab-types: EmailAccountData, OrgMemberOption, AccessLevel
 */

import { useMemo, useState } from 'react'
import { Users, X, UserPlus } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type {
  AccessLevel,
  EmailAccountData,
  OrgMemberOption,
} from './email-accounts-tab-types'

const ACCESS_LEVELS: { value: AccessLevel; label: string }[] = [
  { value: 'read', label: 'View' },
  { value: 'send', label: 'View & send' },
  { value: 'manage', label: 'Manage' },
]

export interface SharedAccessEditorProps {
  account: EmailAccountData
  members: OrgMemberOption[]
  onAddGrant: (accountId: number, userId: number, accessLevel: AccessLevel) => void
  onRemoveGrant: (accountId: number, grantId: number) => void
}

/** Pure I→O: members who can still be added (exclude owner + already-granted). */
export function availableMembers(
  members: OrgMemberOption[],
  ownerId: number | undefined,
  grantedUserIds: Set<number>,
): OrgMemberOption[] {
  return members.filter((m) => m.id !== ownerId && !grantedUserIds.has(m.id))
}

export function SharedAccessEditor({
  account,
  members,
  onAddGrant,
  onRemoveGrant,
}: SharedAccessEditorProps) {
  const grants = account.access_grants ?? []
  const [selectedUser, setSelectedUser] = useState('')
  const [level, setLevel] = useState<AccessLevel>('read')

  const ownerId = account.owner_user_id ?? account.owner?.id
  const grantedIds = useMemo(
    () => new Set(grants.map((g) => g.user_id)),
    [grants],
  )
  const available = useMemo(
    () => availableMembers(members, ownerId, grantedIds),
    [members, ownerId, grantedIds],
  )

  const handleAdd = () => {
    const uid = Number(selectedUser)
    if (!uid) return
    onAddGrant(account.id, uid, level)
    setSelectedUser('')
    setLevel('read')
  }

  return (
    <div className="mb-3 p-2 rounded-lg bg-slate-50 dark:bg-slate-700/50">
      <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" /> Shared with
      </p>

      {grants.length > 0 ? (
        <div className="flex flex-wrap gap-1 mb-2">
          {grants.map((grant) => {
            const label = grant.user_name || grant.user_email
            return (
              <span
                key={grant.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300"
              >
                {label}
                <span className="text-slate-400">({grant.access_level})</span>
                <button
                  type="button"
                  onClick={() => onRemoveGrant(account.id, grant.id)}
                  className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors"
                  title={`Remove ${label}`}
                  aria-label={`Remove ${label}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )
          })}
        </div>
      ) : (
        <p className="text-xs text-slate-400 mb-2">Not shared with anyone yet.</p>
      )}

      {available.length > 0 ? (
        <div className="flex items-center gap-1.5">
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="flex-1 min-w-0 text-xs rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-200"
            aria-label="Choose a person to share with"
          >
            <option value="">Add a person</option>
            {available.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name || m.email}
              </option>
            ))}
          </select>
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value as AccessLevel)}
            className="text-xs rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-200"
            aria-label="Access level"
          >
            {ACCESS_LEVELS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!selectedUser}
            className={cn(
              'inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex-shrink-0',
              selectedUser
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-slate-200 text-slate-400 dark:bg-slate-700 cursor-not-allowed',
            )}
          >
            <UserPlus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
      ) : (
        members.length > 0 && (
          <p className="text-xs text-slate-400">
            Everyone in your organisation already has access.
          </p>
        )
      )}
    </div>
  )
}
